import React from "react";
import {
  ArrayField,
  ChipField,
  Datagrid,
  DatagridConfigurable,
  DateField,
  DateInput,
  FunctionField,
  List,
  NumberField,
  NumberInput,
  Pagination,
  ReferenceField,
  ReferenceManyField,
  SelectField,
  SelectInput,
  Show,
  SimpleList,
  SingleFieldList,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  downloadCSV,
  usePermissions,
  useShowController,
  useTranslate,
  SimpleShowLayout,
} from "react-admin";
import { Box, Stack, Divider, useMediaQuery } from "@mui/material";
import jsonExport from "jsonexport/dist";

import { SpotContractPaymentShow } from "./spot_contract_payment";
import { AddressField } from "../components/AddressField";
import { Alert } from "../components/Alert";
import {
  DEAL_TYPE_CHOICES,
  createDeliveryStatusChoices,
  createDeliveryTypeChoices,
  createHasProducerReviewed,
  createPaymentStatuses,
  createVehicleTypes,
  createHolidayAvailabilities,
} from "../components/choices";
import CreateRelatedResourceButton from "../components/CreateRelatedResourceButton";
import { SpotContractDealShowActions } from "../components/SpotContractDealShowActions";
import { ExistsSpecialConditionsField } from "../components/exists_special_conditions_field";
import { ProducerAgreementStatusField } from "../components/producer_agreement_status_field";
import { WholesalerAgreementStatusField } from "../components/wholesaler_agreement_status";
import { useSpotContractAmend } from "../hooks/useAmend";
import { DeleteDeliveryButton, EditDeliveryButton, InspectDeliveryButton } from "../components/SpotContractAmendButton";
import { AttachmentFileField } from "../components/AttachmentFile";
import {
  formatStringAsCurrency,
  getDateString,
  getFormatNumeric,
  getFormatRateString,
  getFormatTaxRateString,
} from "../utils/locale";
import { SpotContractIndividualDeliveryShow } from "./spot_contract_individual_deliveries";
import { HandoverAtField } from "../components/HandoverAtField";
import { HandoverAddressPhotos } from "../components/HandoverAddressPhotos";
import { mobileDeliveryListStyles } from "../components/MobileDeliveryListStyles";
import { ReceiptTimingField } from "../components/ReceiptTimingField";
import { useSpotContractUnassignedDeliveryContents } from "../hooks/useUnassignedDeliveryContents";
import { InvoiceRegistrationStatusField } from "../components/InvoiceRegistrationStatusField";
import { commonStyles } from "../components/common_styles";
import { ListActions } from "../components/list_actions";
import {
  MobileDealCardField,
  mobileDealCardStyles,
  mobileDealDateLabelStyles,
  mobileDealDateValueStyles,
  mobileDealFieldStyles,
  mobileDealHeaderStyles,
  mobileDealIdStyles,
  mobileDealListStyles,
} from "../components/MobileDealCardStyles";
import { MobileListActions } from "../components/MobileListActions";
import type { MobileListSortOption } from "../components/MobileListSort";
import { getAmendValues, getOfferIdField, getSpotContractIdField } from "../utils/deal";
import { getApiBase } from "../utils/api";
import {
  formatDisplayStoragePeriodBonus,
  formatDisplayTradingVolume,
  formatDisplayUnitPrice,
  formatPriceCalculationMode,
  getDisplayStoragePeriodBonusValue,
  getDisplayTradingVolumeValue,
  getDisplayUnitPriceValue,
  priceCalculationModeChoices,
} from "../utils/price_calculation";
import { DealAmendField } from "../components/DealAmendField";

const statuses = ["closed", "provisional", "not_closed", "done", "cancelled"];
const createStatusChoices = () =>
  statuses.map((status) => ({
    id: status,
    name: `resources.deals.fields.statuses.${status}`,
  }));

const agreementStatuses = ["waiting", "agreement", "disagreement"];
const createAgreementStatusChoices = () =>
  agreementStatuses.map((status) => ({
    id: status,
    name: `commons.agreement_statuses.${status}`,
  }));

const amendStatuses = ["waiting_for_delivery_creation", "waiting_for_after_delivery_inspection", "waiting_for_payment"];
const createAmendStatusChoices = () =>
  amendStatuses.map((status) => ({
    id: status,
    name: `commons.amend_statuses.${status}`,
  }));

const getDisplayStoragePeriodBonusForRecord = (record: any) => {
  return getDisplayStoragePeriodBonusValue(record.storage_period, record.price_calculation_mode);
};

const isChangedStoragePeriodBonus = (record: any, amend: any) => {
  if (!amend) {
    return false;
  }

  return (
    getDisplayStoragePeriodBonusValue(record.storage_period, record.price_calculation_mode) !==
    getDisplayStoragePeriodBonusValue(amend.storage_period, amend.price_calculation_mode)
  );
};

function getExceptionalUnitPriceAdjustmentInYen(amend: any) {
  return () => {
    if (amend) {
      return formatStringAsCurrency(amend["unit_price_adjustment_in_yen"]);
    } else {
      return null;
    }
  };
}

const SpotContractDealFilter = [
  <TextInput key="ids" source="ids" />,
  <NumberInput key="offer_id" source="offer_id" />,
  <SelectInput key="status" source="status" choices={createStatusChoices()} />,
  <DateInput key="handover_deadline_date" source="handover_deadline_date" />,
  <TextInput key="producer_company_name" source="producer_company_name" />,
  <TextInput key="annum_name" source="annum_name" />,
  <TextInput key="prefecture_name" source="prefecture_name" />,
  <TextInput key="region_name" source="region_name" />,
  <TextInput key="brand_name" source="brand_name" />,
  <TextInput key="grade_name" source="grade_name" />,
  <SelectInput
    key="producer_agreement_status"
    source="producer_agreement_status"
    choices={createAgreementStatusChoices()}
  />,
  <SelectInput
    key="wholesaler_agreement_status"
    source="wholesaler_agreement_status"
    choices={createAgreementStatusChoices()}
  />,
  <SelectInput key="without_offer" source="without_offer" choices={DEAL_TYPE_CHOICES} />,
  <SelectInput
    key="amend_status"
    source="amend_status"
    choices={createAmendStatusChoices()}
    label="ui.deals.amend_status"
  />,
];

const MobileSpotContractDealFilter = [
  <TextInput key="ids" source="ids" />,
  <DateInput key="handover_deadline_date" source="handover_deadline_date" />,
  <TextInput key="producer_company_name" source="producer_company_name" />,
  <TextInput key="annum_name" source="annum_name" />,
  <TextInput key="prefecture_name" source="prefecture_name" />,
  <TextInput key="brand_name" source="brand_name" />,
  <TextInput key="grade_name" source="grade_name" />,
];

const mobileSpotContractDealSortOptions: MobileListSortOption[] = [
  { field: "handover_deadline_date", order: "ASC", label: "引取期限（近い順）" },
];

const formatMobileDealCard = (record: any) => (
  <Box component="span" sx={mobileDealCardStyles}>
    <Box component="span" sx={mobileDealHeaderStyles}>
      <Box component="span" sx={mobileDealIdStyles}>
        {getSpotContractIdField(record)}
      </Box>
      {record.amend !== null && (
        <Box component="span" sx={mobileDealDateLabelStyles}>
          引取期限
          <br />
          <Box component="span" sx={mobileDealDateValueStyles}>
            {getDateString(record.amend.handover_deadline_date)}
          </Box>
        </Box>
      )}
    </Box>
    <Box component="span" sx={mobileDealFieldStyles}>
      <MobileDealCardField label="売り手">{record.producer.company_name}</MobileDealCardField>
      <MobileDealCardField label="年産 / 県産">
        {record.annum.name} / {record.prefecture.name}
      </MobileDealCardField>
      <MobileDealCardField label="銘柄 / 等級">
        {record.brand.name} / {record.grade.name}
      </MobileDealCardField>
      <MobileDealCardField label="口数 / 俵数">
        {getFormatNumeric("trading_units")(record)}口 / {getFormatNumeric("trading_sacks")(record)}俵
      </MobileDealCardField>
      <MobileDealCardField label="契約単価">{formatDisplayUnitPrice(getAmendValues(record))}</MobileDealCardField>
    </Box>
  </Box>
);

const export_headers = [
  "id",
  "offer_id",
  "status",
  "amend.handover_deadline_date",
  "producer.company_name",
  "annum.name",
  "prefecture.name",
  "region.name",
  "brand.name",
  "grade.name",
  "packaging_form.name",
  "trading_units",
  "trading_sacks",
  "price_calculation_mode",
  "unit_price",
  "trading_volume_in_yen",
  "special_conditions",
  "special_conditions_memo",
  "producer_agreement_status",
  "wholesaler_agreement_status",
  "agreement_deadline",
  "decision_date",
  "storage_period.name",
  "storage_period.bonus_in_yen",
  "grade_penalties",
];

const exporter = (translate: any) => {
  const renamed_headers = export_headers.map((name) => translate(`resources.spot_contract_deals.fields.${name}`));

  return (deals: any) => {
    const dealsForExport = deals.map((deal: any) => {
      deal.region = deal.region ? deal.region : {};
      deal.amend = deal.amend ? deal.amend : {};

      const partial_deal = export_headers.reduce((result: any, prop) => {
        if (prop.includes(".")) {
          const [object, property] = prop.split(".");
          if (result[object] === undefined) {
            result[object] = {};
          }

          result[object][property] = deal[object][property];
        } else {
          result[prop] = deal[prop];
        }

        return result;
      }, {});

      partial_deal.id = `S${deal.id}`;
      partial_deal.offer_id = deal.offer_id ? deal.offer_id : "なし";
      partial_deal.status = translate(`resources.spot_contract_deals.fields.statuses.${deal.status}`);
      const amendValues = getAmendValues(deal);
      partial_deal.price_calculation_mode = formatPriceCalculationMode(amendValues);
      partial_deal.amend.handover_deadline_date = getDateString(deal.amend.handover_deadline_date);
      partial_deal.decision_date = getDateString(deal.decision_date);
      partial_deal.producer_agreement_status = translate(
        `commons.agreement_statuses.${deal.producer_agreement_status}`
      );
      partial_deal.wholesaler_agreement_status = translate(
        `commons.agreement_statuses.${deal.wholesaler_agreement_status}`
      );
      partial_deal.agreement_deadline = getDateString(deal.agreement_deadline);
      const gradePenalties = deal.amend?.grade_penalties ?? deal.grade_penalties;
      partial_deal.grade_penalties =
        gradePenalties.length > 0
          ? gradePenalties
              .map(
                (item: any) =>
                  item.grade.name + "：" + (item.penalty_in_yen === null ? "受け取らない" : item.penalty_in_yen + "円")
              )
              .join("、")
          : "";
      partial_deal.special_conditions =
        deal.special_conditions.length > 0
          ? deal.special_conditions.map((item: any) => item.display_text).join("、")
          : "";
      partial_deal.unit_price = getDisplayUnitPriceValue(amendValues);
      partial_deal.trading_volume_in_yen = getDisplayTradingVolumeValue(amendValues);
      partial_deal.storage_period.name = amendValues?.storage_period?.name;
      partial_deal.storage_period.bonus_in_yen = getDisplayStoragePeriodBonusForRecord(amendValues);

      return partial_deal;
    });

    jsonExport(
      dealsForExport,
      {
        headers: export_headers,
        rename: renamed_headers,
      },
      (err, csv) => {
        downloadCSV("\ufeff" + csv, "現物契約_取引案件");
      }
    );
  };
};

export const SpotContractDealList = (props: any) => {
  const translate = useTranslate();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));
  const filters = isMobile ? MobileSpotContractDealFilter : SpotContractDealFilter;
  const preferenceKey = "spot_contract_deals";

  return (
    <List
      {...props}
      actions={
        isMobile ? (
          <MobileListActions filters={filters} sortOptions={mobileSpotContractDealSortOptions} />
        ) : (
          <ListActions filters={filters} preferenceKey={preferenceKey} savedFilterScope="spot_contract_deals" />
        )
      }
      filters={filters}
      sort={{ field: "id", order: "DESC" }}
      exporter={exporter(translate)}
    >
      {isMobile ? (
        <Box sx={mobileDealListStyles}>
          <SimpleList linkType="show" primaryText={formatMobileDealCard} />
        </Box>
      ) : (
        <>
          <DatagridConfigurable bulkActionButtons={false} preferenceKey={preferenceKey} rowClick="show">
            <FunctionField source="id" render={getSpotContractIdField} />
            <FunctionField source="offer_id" render={getOfferIdField()} />
            <SelectField source="status" choices={createStatusChoices()} sortable={false} />
            <DateField source="amend.handover_deadline_date" sortable={true} sortBy="handover_deadline_date" />
            <TextField source="producer.company_name" sortable={false} />
            <TextField source="annum.name" sortable={false} />
            <TextField source="prefecture.name" sortable={false} />
            <TextField source="region.name" sortable={false} />
            <TextField source="brand.name" sortable={false} />
            <TextField source="grade.name" sortable={false} />
            <TextField source="packaging_form.name" sortable={false} />
            <NumberField source="trading_units" sortable={false} />
            <NumberField source="trading_sacks" sortable={false} />
            <FunctionField
              source="price_calculation_mode"
              render={(record: any) => formatPriceCalculationMode(getAmendValues(record))}
              sortable={false}
            />
            <FunctionField
              source="unit_price"
              render={(record: any) => formatDisplayUnitPrice(getAmendValues(record))}
              sortable={false}
              textAlign="right"
            />
            <FunctionField
              source="trading_volume_in_yen"
              render={(record: any) => formatDisplayTradingVolume(getAmendValues(record))}
              sortable={false}
              textAlign="right"
            />
            <ExistsSpecialConditionsField source="special_condition_ids" sortable={false} />
            <ProducerAgreementStatusField source="producer_agreement_status" sortable={false} />
            <WholesalerAgreementStatusField source="wholesaler_agreement_status" sortable={false} />
            <DateField source="agreement_deadline" locales={"ja"} />
            <DateField source="decision_date" locales={"ja"} />
          </DatagridConfigurable>
        </>
      )}
    </List>
  );
};

const formatStoragePeriodBonusRecord = (record: any) => {
  return formatDisplayStoragePeriodBonus(record.storage_period, record.price_calculation_mode);
};

const mobilePaymentListStyles = {
  ...mobileDeliveryListStyles,
  "& .payment-id": {
    width: "24%",
  },
  "& .payment-summary": {
    width: "76%",
  },
};

export const SpotContractDealShow = (props: any) => {
  const { permissions } = usePermissions();
  const { record } = useShowController(props);
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));
  const isClosedOrDone = record?.status === "closed" || record?.status === "done";
  const isCancelled = record?.status === "cancelled";
  const isPrenegotiatedDeal = record?.offer_id === null;
  const hasAmendStatus = isClosedOrDone || isCancelled;

  const amendId = hasAmendStatus ? record.id : undefined;
  const [loadingAmend, amend] = useSpotContractAmend(amendId);
  const [loadingUnassignedDeliveryContents, unassignedDeliveryContents] =
    useSpotContractUnassignedDeliveryContents(amendId);
  const translate = useTranslate();
  if (!record || record.status === undefined || loadingAmend || loadingUnassignedDeliveryContents) {
    return null;
  }

  const isDeliveryCreationStep = amend?.current_step_buyer === "waiting_for_delivery_creation";
  const isDeliveryInspectionStep = amend?.current_step_buyer === "waiting_for_after_delivery_inspection";
  const hasWritePermission = permissions === "admin" || permissions === "staff";
  const hasUnassignedDeliveryContents = unassignedDeliveryContents?.some((e) => e.package_count > 0);
  const isExceptionFlow = amend?.is_exception_flow === true;

  const isChangedStoragePeriod = hasAmendStatus && record.storage_period.id !== amend?.storage_period.id;
  const isChangedStoragePeriodBonusInYen = isChangedStoragePeriodBonus(record, amend);
  const isChangedPriceCalculationMode =
    hasAmendStatus && record.price_calculation_mode !== amend?.price_calculation_mode;
  const isChangedPrice =
    hasAmendStatus && amend != null && getDisplayUnitPriceValue(record) !== getDisplayUnitPriceValue(amend);
  const canShowAmendAdjustmentInYen =
    hasAmendStatus &&
    record.unit_price_adjustment_in_yen != amend?.unit_price_adjustment_in_yen &&
    amend?.unit_price_adjustment_in_yen !== 0;
  const canUpdateDelivery =
    (isDeliveryCreationStep || isDeliveryInspectionStep) && hasWritePermission && !isExceptionFlow;
  const canCreateDelivery = canUpdateDelivery && hasUnassignedDeliveryContents;

  return (
    <Show
      {...props}
      actions={<SpotContractDealShowActions listPath="/spot_contract_deals" permissions={permissions} />}
    >
      <TabbedShowLayout sx={commonStyles.mobileTabbedShowLayout}>
        <Tab label="ui.deals.tabs.info">
          <FunctionField source="id" render={getSpotContractIdField} />
          <FunctionField source="offer_id" render={getOfferIdField()} />
          <SelectField source="status" choices={createStatusChoices()} />
          <DateField source="decision_date" locales={"ja"} />
          <ProducerAgreementStatusField source="producer_agreement_status" showTime={true} />
          <WholesalerAgreementStatusField source="wholesaler_agreement_status" showTime={true} />
          <DateField source="agreement_deadline" locales={"ja"} />
          <TextField source="producer.company_name" />
          <InvoiceRegistrationStatusField source="producer.invoice_registration_status" />
          <DateField source="created_at" locales={"ja"} showTime={true} />
        </Tab>
        <Tab path="contract" label="ui.deals.tabs.contract">
          <TextField source="annum.name" />
          <TextField source="prefecture.name" />
          <TextField source="region.name" />
          <TextField source="brand.name" />
          <TextField source="grade.name" />
          <TextField source="packaging_form.name" />
          <NumberField source="trading_units" />
          <NumberField source="trading_sacks" />
          <NumberField source="trading_kg" />
          <NumberField source="package_count" />
          {!isChangedPriceCalculationMode && (
            <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} />
          )}
          {isChangedPriceCalculationMode && (
            <DealAmendField
              amendSource="price_calculation_mode"
              source="price_calculation_mode"
              choices={priceCalculationModeChoices}
            />
          )}
          {isMobile && !isPrenegotiatedDeal && <NumberField source="offer_unit_price" />}
          {!isChangedPrice && <FunctionField source="unit_price" render={formatDisplayUnitPrice} />}
          {isChangedPrice && (
            <DealAmendField amendSource="unit_price" source="unit_price" formatter={formatDisplayUnitPrice} />
          )}
          {!isChangedPrice && <FunctionField source="trading_volume_in_yen" render={formatDisplayTradingVolume} />}
          {isChangedPrice && (
            <DealAmendField
              amendSource="trading_volume_in_yen"
              source="trading_volume_in_yen"
              formatter={formatDisplayTradingVolume}
            />
          )}
          <NumberField source="unit_price_adjustment_in_yen" />
          {/* NumberFieldにrecordオプションを渡しても表示されない不具合のため、FunctionFieldで実装 */}
          {canShowAmendAdjustmentInYen && (
            <FunctionField
              source="unit_price_adjustment_in_yen"
              label="ui.amends.exceptional_unit_price_adjustment_in_yen"
              render={getExceptionalUnitPriceAdjustmentInYen(amend)}
            />
          )}
          <TextField source="storage_period.name" />
          {!(isChangedStoragePeriod || isChangedStoragePeriodBonusInYen) && (
            <FunctionField source="storage_period.bonus_in_yen" render={formatStoragePeriodBonusRecord} />
          )}
          {(isChangedStoragePeriod || isChangedStoragePeriodBonusInYen) && (
            <DealAmendField
              amendSource="storage_period.bonus_in_yen"
              source="storage_period.bonus_in_yen"
              formatter={formatStoragePeriodBonusRecord}
            />
          )}
          {!hasAmendStatus && (
            <ArrayField source="grade_penalties">
              <Box sx={commonStyles.showPageInnerDatagrid}>
                <Datagrid bulkActionButtons={false} empty={null}>
                  <NumberField source="grade.name" />
                  <NumberField source="penalty_in_yen" emptyText="受け取らない" />
                </Datagrid>
              </Box>
            </ArrayField>
          )}
          {hasAmendStatus && (
            <ReferenceField
              reference="spot_contract_amends"
              source="id"
              label="resources.spot_contract_amends.fields.grade_penalties"
              link={false}
            >
              <ArrayField source="grade_penalties">
                <Box sx={commonStyles.showPageInnerDatagrid}>
                  <Datagrid bulkActionButtons={false} empty={null}>
                    <NumberField source="grade.name" />
                    <NumberField source="penalty_in_yen" emptyText="受け取らない" />
                  </Datagrid>
                </Box>
              </ArrayField>
            </ReferenceField>
          )}
          <ArrayField source="special_conditions">
            <SingleFieldList linkType={false}>
              <ChipField source="name" />
            </SingleFieldList>
          </ArrayField>
          <TextField source="special_conditions_memo" component="pre" />
        </Tab>
        {!isMobile && !isPrenegotiatedDeal && (
          <Tab path="offer" label="ui.deals.tabs.offer">
            <NumberField source="offer_unit_price" />
          </Tab>
        )}
        {isClosedOrDone && (
          <Tab path="delivery" label="ui.deals.tabs.delivery">
            {isExceptionFlow && (
              <Alert severity="warning">
                {translate("ui.deals.in_exception_flow")
                  .split("\n")
                  .map((line: string) => {
                    return <div key={line}>{line}</div>;
                  })}
              </Alert>
            )}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <SimpleShowLayout sx={{ flex: 1, padding: "0px" }}>
                <h3>契約情報</h3>
                <FunctionField source="id" render={getSpotContractIdField} />
                <TextField source="producer.company_name" />
                <TextField source="annum.name" />
                <TextField source="prefecture.name" />
                <TextField source="region.name" />
                <TextField source="brand.name" />
                <TextField source="grade.name" />
                <TextField source="packaging_form.name" />
                <NumberField source="trading_sacks" />
                {!isChangedPriceCalculationMode && (
                  <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} />
                )}
                {isChangedPriceCalculationMode && (
                  <DealAmendField
                    amendSource="price_calculation_mode"
                    source="price_calculation_mode"
                    choices={priceCalculationModeChoices}
                  />
                )}
                {!isChangedPrice && <FunctionField source="unit_price" render={formatDisplayUnitPrice} />}
                {isChangedPrice && (
                  <DealAmendField amendSource="unit_price" source="unit_price" formatter={formatDisplayUnitPrice} />
                )}
                {/* NumberFieldにrecordオプションを渡しても表示されない不具合のため、FunctionFieldで実装 */}
                {canShowAmendAdjustmentInYen && (
                  <FunctionField
                    source="unit_price_adjustment_in_yen"
                    label="ui.amends.exceptional_unit_price_adjustment_in_yen"
                    render={getExceptionalUnitPriceAdjustmentInYen(amend)}
                  />
                )}
                {!(isChangedStoragePeriod || isChangedStoragePeriodBonusInYen) && (
                  <FunctionField source="storage_period.bonus_in_yen" render={formatStoragePeriodBonusRecord} />
                )}
                {(isChangedStoragePeriod || isChangedStoragePeriodBonusInYen) && (
                  <DealAmendField
                    amendSource="storage_period.bonus_in_yen"
                    source="storage_period.bonus_in_yen"
                    formatter={formatStoragePeriodBonusRecord}
                  />
                )}
                {!hasAmendStatus && (
                  <ArrayField source="grade_penalties">
                    <Box sx={commonStyles.showPageInnerDatagrid}>
                      <Datagrid bulkActionButtons={false} empty={null}>
                        <NumberField source="grade.name" />
                        <NumberField source="penalty_in_yen" emptyText="受け取らない" />
                      </Datagrid>
                    </Box>
                  </ArrayField>
                )}
                {hasAmendStatus && (
                  <ReferenceField
                    reference="spot_contract_amends"
                    source="id"
                    label="resources.spot_contract_amends.fields.grade_penalties"
                    link={false}
                  >
                    <ArrayField source="grade_penalties">
                      <Box sx={commonStyles.showPageInnerDatagrid}>
                        <Datagrid bulkActionButtons={false} empty={null}>
                          <NumberField source="grade.name" />
                          <NumberField source="penalty_in_yen" emptyText="受け取らない" />
                        </Datagrid>
                      </Box>
                    </ArrayField>
                  </ReferenceField>
                )}
              </SimpleShowLayout>
              <SimpleShowLayout sx={{ flex: 1, padding: "0px" }}>
                <h3>売り手の引取情報</h3>
                <DateField
                  source="decision_date"
                  label="resources.spot_contract_individual_deliveries.fields.decision_date"
                  emptyText={translate("ui.value.not_set")}
                />
                <TextField source="storage_period.name" />
                <DateField source="amend.handover_deadline_date" />
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.handover_address"
                  link={false}
                >
                  <>
                    <TextField source="handover_address.name" />
                    <br />
                    <Box sx={{ wordBreak: "break-all" }}>
                      <AddressField source="handover_address" />
                    </Box>
                  </>
                </ReferenceField>
                <HandoverAddressPhotos
                  downloadUrl={`${getApiBase()}/spot_contract_deals/${record.id}/handover_address_photos/download`}
                  label="resources.spot_contract_amends.fields.handover_address_photo_attachments"
                  photos={amend?.handover_address?.photo_attachments ?? []}
                />
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.staff_name_kana"
                  link={false}
                >
                  <TextField source="handover_address.staff_name_kana" />
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.tel"
                  link={false}
                >
                  <TextField source="handover_address.tel" />
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.max_passable_vehicle_type"
                  link={false}
                >
                  <SelectField source="handover_address.max_passable_vehicle_type" choices={createVehicleTypes()} />
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.holiday_availability"
                  link={false}
                >
                  <SelectField source="handover_address.holiday_availability" choices={createHolidayAvailabilities()} />
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.holiday_availability_others"
                  link={false}
                >
                  <TextField
                    source="handover_address.holiday_availability_others"
                    component="div"
                    sx={commonStyles.showPageWordBreakTextField}
                  />
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.handover_memo"
                  link={false}
                >
                  <TextField source="handover_memo" component="div" sx={commonStyles.showPageWordBreakTextField} />
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.cautions_during_rain"
                  link={false}
                >
                  <TextField
                    source="handover_address.cautions_during_rain"
                    component="div"
                    sx={commonStyles.showPageWordBreakTextField}
                  />
                </ReferenceField>
                <ArrayField source="special_conditions">
                  <SingleFieldList linkType={false}>
                    <ChipField source="name" />
                  </SingleFieldList>
                </ArrayField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.special_conditions_memo"
                  link={false}
                >
                  <TextField
                    source="special_conditions_memo"
                    component="div"
                    sx={commonStyles.showPageWordBreakTextField}
                  />
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.inspection_results"
                  link={false}
                >
                  <ArrayField source="inspection_results">
                    <Box sx={commonStyles.showPageInnerDatagrid}>
                      <Datagrid bulkActionButtons={false} empty={null}>
                        <TextField source="grade.name" emptyText={translate("ui.value.default_grade")} />
                        <NumberField source="package_count" />
                      </Datagrid>
                    </Box>
                  </ArrayField>
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.inspection_results_memo"
                  link={false}
                >
                  <TextField
                    source="inspection_results_memo"
                    component="div"
                    sx={commonStyles.showPageWordBreakTextField}
                  />
                </ReferenceField>
                <ReferenceField
                  reference="spot_contract_amends"
                  source="id"
                  label="resources.spot_contract_amends.fields.inspection_results_attachments"
                  link={false}
                >
                  <ArrayField source="inspection_results_attachments">
                    <SingleFieldList linkType={false}>
                      <AttachmentFileField
                        styles={{
                          width: "100%",
                          my: 1,
                        }}
                      />
                    </SingleFieldList>
                  </ArrayField>
                </ReferenceField>
              </SimpleShowLayout>
            </Stack>
            <Divider />
            <h3>個別運送情報</h3>
            <ReferenceManyField
              reference="spot_contract_individual_deliveries"
              target="amend_id"
              label={false}
              pagination={<Pagination />}
            >
              <Box sx={isMobile ? { width: "100%" } : commonStyles.showPageInnerDatagrid}>
                {isMobile ? (
                  <Datagrid
                    bulkActionButtons={false}
                    expand={<SpotContractIndividualDeliveryShow canUpdateDelivery={canUpdateDelivery} />}
                    rowClick="expand"
                    sx={mobileDeliveryListStyles}
                  >
                    <TextField
                      source="id"
                      label="resources.spot_contract_individual_deliveries.fields.mobile_id"
                      sortable={false}
                      headerClassName="delivery-id"
                      cellClassName="delivery-id"
                    />
                    <HandoverAtField
                      label="resources.spot_contract_individual_deliveries.fields.handover_at"
                      sortable={false}
                      headerClassName="delivery-handover-at"
                      cellClassName="delivery-handover-at"
                    />
                    <FunctionField
                      source="delivery_type"
                      label="resources.spot_contract_individual_deliveries.fields.mobile_delivery_type_and_status"
                      render={(record: any) =>
                        `${translate(`commons.delivery_types.${record.delivery_type}`)}\n${translate(
                          `commons.delivery_statuses.${record.status}`
                        )}`
                      }
                      sortable={false}
                      headerClassName="delivery-summary"
                      cellClassName="delivery-summary"
                    />
                  </Datagrid>
                ) : (
                  <Datagrid
                    bulkActionButtons={false}
                    expand={<SpotContractIndividualDeliveryShow canUpdateDelivery={canUpdateDelivery} />}
                    sx={commonStyles.showPageInnerList}
                    rowClick="expand"
                  >
                    <TextField source="id" label={"resources.spot_contract_individual_deliveries.fields.id"} />
                    <SelectField
                      source="delivery_type"
                      choices={createDeliveryTypeChoices()}
                      label="resources.spot_contract_individual_deliveries.fields.delivery_type"
                      sortable={false}
                    />
                    <SelectField
                      source="status"
                      choices={createDeliveryStatusChoices()}
                      label="resources.spot_contract_individual_deliveries.fields.status"
                      sortable={false}
                    />
                    <SelectField
                      source="has_producer_reviewed"
                      choices={createHasProducerReviewed()}
                      label="resources.spot_contract_individual_deliveries.fields.has_producer_reviewed"
                      sortable={false}
                    />
                    <ReferenceField
                      source="delivery_address.id"
                      label="resources.spot_contract_individual_deliveries.fields.delivery_address.id"
                      reference="wholesaler_addresses"
                      link={false}
                      sortable={false}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                    <HandoverAtField
                      label="resources.spot_contract_individual_deliveries.fields.hacobell.handover_timing"
                      sortable={false}
                    />
                    <ReceiptTimingField label="resources.spot_contract_individual_deliveries.fields.receipt_timing" />
                    {canUpdateDelivery && <EditDeliveryButton />}
                    {canUpdateDelivery && <DeleteDeliveryButton />}
                    {canUpdateDelivery && <InspectDeliveryButton />}
                  </Datagrid>
                )}
              </Box>
            </ReferenceManyField>
            {canCreateDelivery && (
              <ReferenceField reference="spot_contract_amends" source="id" label="" link={false}>
                <CreateRelatedResourceButton
                  reference="spot_contract_individual_deliveries"
                  target="amend_id"
                  hasVersion={true}
                  permissions={permissions}
                />
              </ReferenceField>
            )}
          </Tab>
        )}
        {isClosedOrDone && (
          <Tab path="payment" label="ui.deals.tabs.payments">
            <TextField source="annum.name" />
            <TextField source="prefecture.name" />
            <TextField source="brand.name" />
            <ReferenceField
              reference="spot_contract_amends"
              source="id"
              label="resources.spot_contract_amends.fields.producer"
              link={false}
            >
              <TextField source="producer.company_name" />
            </ReferenceField>
            <InvoiceRegistrationStatusField source="producer.invoice_registration_status" />
            <ReferenceField
              reference="spot_contract_amends"
              source="id"
              label="resources.spot_contract_amends.fields.commission_rate"
              link={false}
            >
              <FunctionField
                source="commission_rate"
                render={getFormatRateString("commission_rate")}
                sortable={false}
              />
            </ReferenceField>
            <ReferenceField
              reference="spot_contract_amends"
              source="id"
              label="resources.spot_contract_deals.fields.price_calculation_mode"
              link={false}
            >
              <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} />
            </ReferenceField>

            {amend?.price_calculation_mode === "exclusive" && (
              <ReferenceField
                reference="spot_contract_amends"
                source="id"
                label="resources.spot_contract_payments.fields.product_tax_rate"
                link={false}
              >
                <FunctionField
                  source="product_tax_rate"
                  render={getFormatTaxRateString("product_tax_rate")}
                  sortable={false}
                />
              </ReferenceField>
            )}
            {amend?.price_calculation_mode === "exclusive" && (
              <ReferenceField
                reference="spot_contract_amends"
                source="id"
                label="resources.spot_contract_payments.fields.others_tax_rate"
                link={false}
              >
                <FunctionField
                  source="others_tax_rate"
                  render={getFormatTaxRateString("others_tax_rate")}
                  sortable={false}
                />
              </ReferenceField>
            )}
            <ReferenceManyField
              reference="spot_contract_payments"
              target="amend_id"
              label="resources.spot_contract_payments.name"
              pagination={<Pagination />}
            >
              <Box sx={isMobile ? { width: "100%" } : commonStyles.showPageInnerDatagrid}>
                {isMobile ? (
                  <Datagrid
                    bulkActionButtons={false}
                    expand={<SpotContractPaymentShow resource="spot_contract_payments" permissions={permissions} />}
                    rowClick="expand"
                    sx={mobilePaymentListStyles}
                  >
                    <TextField
                      source="id"
                      label="支払ID"
                      sortable={false}
                      headerClassName="payment-id"
                      cellClassName="payment-id"
                    />
                    <FunctionField
                      source="status"
                      label="resources.spot_contract_payments.fields.mobile_status_and_payment_due_date"
                      render={(record: any) =>
                        `${translate(`commons.payment_statuses.${record.status}`)}\n支払期日 ${getDateString(
                          record.payment_due_date
                        )}`
                      }
                      sortable={false}
                      headerClassName="payment-summary"
                      cellClassName="payment-summary"
                    />
                  </Datagrid>
                ) : (
                  <Datagrid
                    bulkActionButtons={false}
                    expand={<SpotContractPaymentShow resource="spot_contract_payments" permissions={permissions} />}
                    rowClick="expand"
                  >
                    <TextField source="id" label={"resources.payments.fields.id"} />
                    <SelectField source="status" choices={createPaymentStatuses()} sortable={false} />
                    <ReferenceField
                      source="individual_delivery_id"
                      reference="spot_contract_individual_deliveries"
                      label="resources.individual_deliveries.fields.handover_at"
                      link={false}
                      sortable={false}
                    >
                      <HandoverAtField
                        label="resources.individual_deliveries.fields.hacobell.handover_at"
                        sortable={false}
                      />
                    </ReferenceField>
                    <DateField source="payment_due_date" locales="ja" sortable={false} />
                    <FunctionField
                      source="total_bill_in_yen"
                      render={getFormatNumeric("total_bill_in_yen")}
                      sortable={false}
                    />
                  </Datagrid>
                )}
              </Box>
            </ReferenceManyField>
          </Tab>
        )}
      </TabbedShowLayout>
    </Show>
  );
};
